// ============================================
// moldClaw License API Client
// ============================================

/**
 * 🧑‍💻 SWE-2: API 연동 & 에러 핸들링
 * 🔒 Sec-3: 입력 검증 & 토큰 관리
 */

import { supabase } from './supabase';

// API Base URL - 환경별 분기
const API_BASE = import.meta.env.VITE_LICENSE_API_URL || 'https://license.moldclaw.com';

// ============================================
// Security: URL 검증
// ============================================

// 허용된 리다이렉트 도메인 (Polar 및 자체 도메인)
const ALLOWED_REDIRECT_DOMAINS = [
  'checkout.polar.sh',
  'polar.sh',
  'moldclaw.com',
  'www.moldclaw.com',
  'license.moldclaw.com',
];

/**
 * 리다이렉트 URL 보안 검증
 * Open Redirect 공격 방지
 */
export function validateRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    
    // HTTPS 강제 (개발 환경 localhost 제외)
    if (parsed.protocol !== 'https:' && parsed.hostname !== 'localhost') {
      console.warn('[Security] Non-HTTPS redirect blocked:', parsed.hostname);
      return false;
    }
    
    // localhost는 개발 환경에서만 허용
    if (parsed.hostname === 'localhost') {
      if (import.meta.env.PROD) {
        console.warn('[Security] Localhost redirect blocked in production');
        return false;
      }
      return true;
    }
    
    // 허용 도메인 체크
    const isAllowed = ALLOWED_REDIRECT_DOMAINS.some(domain => 
      parsed.hostname === domain || parsed.hostname.endsWith('.' + domain)
    );
    
    if (!isAllowed) {
      console.warn('[Security] Redirect to unauthorized domain blocked:', parsed.hostname);
      return false;
    }
    
    return true;
  } catch {
    console.warn('[Security] Invalid redirect URL blocked:', url);
    return false;
  }
}

/**
 * 안전한 리다이렉트 수행
 * 검증 실패 시 fallback URL로 이동
 */
export function safeRedirect(url: string, fallbackPath: string = '/pricing'): void {
  if (validateRedirectUrl(url)) {
    window.location.href = url;
  } else {
    console.error('[Security] Redirect blocked, navigating to fallback:', fallbackPath);
    window.location.href = fallbackPath;
  }
}

// ============================================
// Types
// ============================================

export interface License {
  code: string;
  tier: 'free' | 'pro' | 'team';
  status: 'active' | 'suspended' | 'expired';
  valid_from: string;
  valid_until: string | null;
  devices_used: number;
  max_devices: number;
}

export interface Device {
  id: string;
  name: string;
  platform: string;
  last_seen_at: string;
  is_current: boolean;
}

export interface CheckoutSession {
  url: string;
}

export interface ApiError {
  code: string;
  message: string;
}

export class ApiException extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'ApiException';
  }
}

// ============================================
// Token Management (라이선스 JWT)
// ============================================

let licenseAccessToken: string | null = null;
let licenseRefreshToken: string | null = null;

export function setLicenseTokens(accessToken: string, refreshToken: string) {
  licenseAccessToken = accessToken;
  licenseRefreshToken = refreshToken;
  
  // Persist to localStorage (클라이언트 앱용 - 웹에선 optional)
  try {
    localStorage.setItem('moldclaw_access_token', accessToken);
    localStorage.setItem('moldclaw_refresh_token', refreshToken);
  } catch {
    // localStorage 사용 불가 환경
  }
}

export function clearLicenseTokens() {
  licenseAccessToken = null;
  licenseRefreshToken = null;
  try {
    localStorage.removeItem('moldclaw_access_token');
    localStorage.removeItem('moldclaw_refresh_token');
  } catch {
    // ignore
  }
}

export function getLicenseTokens() {
  if (licenseAccessToken && licenseRefreshToken) {
    return { accessToken: licenseAccessToken, refreshToken: licenseRefreshToken };
  }
  
  try {
    const stored = {
      accessToken: localStorage.getItem('moldclaw_access_token'),
      refreshToken: localStorage.getItem('moldclaw_refresh_token'),
    };
    if (stored.accessToken && stored.refreshToken) {
      licenseAccessToken = stored.accessToken;
      licenseRefreshToken = stored.refreshToken;
      return stored;
    }
  } catch {
    // ignore
  }
  
  return null;
}

// ============================================
// Core API Functions
// ============================================

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}/api/v1${endpoint}`;
  
  const optHeaders = options.headers as Record<string, string> || {};
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // 🔧 FIX: options.headers에 Authorization이 명시적으로 있으면 그걸 사용
  // 없으면 라이선스 토큰 사용 (fallback)
  if (optHeaders['Authorization']) {
    headers['Authorization'] = optHeaders['Authorization'];
  } else {
    const tokens = getLicenseTokens();
    if (tokens?.accessToken) {
      headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }
  }
  
  // 나머지 headers 병합 (Authorization 제외)
  for (const [key, value] of Object.entries(optHeaders)) {
    if (key !== 'Authorization') {
      headers[key] = value;
    }
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  const data = await response.json();
  
  if (!response.ok || data.success === false) {
    const error = data.error || { code: 'UNKNOWN_ERROR', message: 'Unknown error occurred' };
    
    // 토큰 만료 시 자동 갱신 시도 (라이선스 토큰인 경우에만)
    const tokens = getLicenseTokens();
    if (response.status === 401 && tokens?.refreshToken && endpoint !== '/license/refresh' && !optHeaders['Authorization']) {
      const refreshed = await refreshLicenseToken();
      if (refreshed) {
        // 원래 요청 재시도
        return fetchApi<T>(endpoint, options);
      }
    }
    
    throw new ApiException(error.code, error.message, response.status);
  }
  
  return data as T;
}

// ============================================
// License API
// ============================================

/**
 * 라이선스 활성화 (데스크톱 앱용)
 */
export async function activateLicense(
  licenseCode: string,
  deviceFingerprint: string,
  deviceName?: string,
  platform?: string
) {
  const result = await fetchApi<{
    success: true;
    access_token: string;
    refresh_token: string;
    license: License;
    device: { id: string; name: string };
  }>('/license/activate', {
    method: 'POST',
    body: JSON.stringify({
      license_code: licenseCode,
      device_fingerprint: deviceFingerprint,
      device_name: deviceName,
      platform: platform,
    }),
  });
  
  setLicenseTokens(result.access_token, result.refresh_token);
  
  return {
    license: result.license,
    device: result.device,
  };
}

/**
 * 라이선스 토큰 갱신
 */
export async function refreshLicenseToken(): Promise<boolean> {
  const tokens = getLicenseTokens();
  if (!tokens?.refreshToken) return false;
  
  try {
    const result = await fetchApi<{
      success: true;
      access_token: string;
      refresh_token: string;
    }>('/license/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: tokens.refreshToken }),
    });
    
    setLicenseTokens(result.access_token, result.refresh_token);
    return true;
  } catch {
    clearLicenseTokens();
    return false;
  }
}

/**
 * 라이선스 상태 조회
 */
export async function getLicenseStatus(): Promise<License> {
  const result = await fetchApi<{ success: true; license: License }>('/license/status');
  return result.license;
}

/**
 * 등록된 기기 목록 조회
 */
export async function getDevices(): Promise<Device[]> {
  const result = await fetchApi<{ success: true; devices: Device[] }>('/license/devices');
  return result.devices;
}

/**
 * 기기 제거
 */
export async function removeDevice(deviceId: string): Promise<void> {
  await fetchApi(`/license/devices/${deviceId}`, { method: 'DELETE' });
}

// ============================================
// Polar Checkout API
// ============================================

export interface PolarCheckoutResponse {
  checkout_url: string;
  checkout_id: string;
}

export interface PolarSubscription {
  id: string;
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'trialing' | 'paused';
  tier: 'free' | 'pro' | 'team';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;  // 🔧 FIX: 백엔드 DB 필드명과 일치 (British spelling)
  // 추가 필드 (백엔드 응답에 포함됨)
  billing_cycle?: 'monthly' | 'yearly';
  amount?: number;
  currency?: string;
  next_billing_date?: string | null;
}

export interface PolarCheckoutStatus {
  id: string;
  status: 'open' | 'confirmed' | 'failed' | 'expired';
  subscription_id: string | null;
  customer_email: string | null;
  amount: number;
  currency: string;
  product_name: string;
}

/**
 * Polar Checkout 세션 생성
 * 백엔드에서 Polar checkout URL을 생성하고 반환
 */
export async function createPolarCheckout(
  tier: 'starter' | 'pro' | 'team',
  email?: string
): Promise<PolarCheckoutResponse> {
  // Supabase 세션에서 이메일 가져오기 (로그인 상태면)
  let userEmail = email;
  if (!userEmail) {
    const { data: { session } } = await supabase.auth.getSession();
    userEmail = session?.user?.email;
  }
  
  const { data: { session } } = await supabase.auth.getSession();
  
  // 🔧 FIX: 백엔드 기대 필드명 camelCase로 통일, 인증 헤더 Authorization 사용
  const result = await fetchApi<{
    success: true;
    data: {
      checkoutUrl: string;
      checkoutId: string;
      clientSecret: string;
      expiresAt: string;
    };
  }>('/payments/checkout', {
    method: 'POST',
    headers: session ? {
      'Authorization': `Bearer ${session.access_token}`,
    } : {},
    body: JSON.stringify({
      tier,
      successUrl: `${window.location.origin}/checkout/success?checkout_id={CHECKOUT_ID}`,
      cancelUrl: `${window.location.origin}/checkout/failed?code=USER_CANCEL&tier=${tier}`,
    }),
  });
  
  return {
    checkout_url: result.data.checkoutUrl,
    checkout_id: result.data.checkoutId,
  };
}

/**
 * Polar Checkout 상태 조회
 * 결제 완료 후 구독 활성화 여부 확인용
 */
export async function getPolarCheckoutStatus(
  checkoutId: string
): Promise<PolarCheckoutStatus> {
  // 🔧 FIX: 올바른 엔드포인트 경로 사용
  const result = await fetchApi<{
    success: true;
    data: {
      checkout: PolarCheckoutStatus;
    };
  }>(`/payments/checkout/${checkoutId}/status`, {
    method: 'GET',
  });
  
  return result.data.checkout;
}

/**
 * 현재 사용자의 Polar 구독 상태 조회
 */
export async function getPolarSubscription(): Promise<PolarSubscription | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  try {
    // 🔧 FIX: 올바른 엔드포인트 및 인증 헤더 사용
    const result = await fetchApi<{
      success: true;
      data: {
        subscription: PolarSubscription | null;
      };
    }>('/payments/subscription', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
    });
    
    return result.data.subscription;
  } catch (err) {
    // 구독이 없는 경우 null 반환
    if (err instanceof ApiException && err.code === 'NOT_FOUND') {
      return null;
    }
    throw err;
  }
}

/**
 * Polar 구독 취소
 */
export async function cancelPolarSubscription(
  reason?: string,
  immediate?: boolean
): Promise<{ message: string; cancelAt: string; immediate: boolean }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  // 🔧 FIX: 올바른 엔드포인트 및 인증 헤더 사용
  const result = await fetchApi<{
    success: true;
    data: {
      message: string;
      cancelAt: string;
      immediate: boolean;
    };
  }>('/payments/cancel-subscription', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ reason, immediate }),
  });
  
  return result.data;
}

/**
 * Polar Customer Portal로 이동 (구독 관리)
 */
export async function createPolarPortalSession(): Promise<{ url: string; message?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  // 🔧 FIX: 올바른 엔드포인트 및 인증 헤더 사용
  const result = await fetchApi<{
    success: true;
    data: {
      portalUrl: string;
      message?: string;
    };
  }>('/billing-keys/update-payment-method', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      returnUrl: `${window.location.origin}/dashboard`,
    }),
  });
  
  return { url: result.data.portalUrl, message: result.data.message };
}

// ============================================
// Legacy Stripe API (deprecated, 호환성 유지)
// ============================================

/**
 * @deprecated Use createPolarCheckout instead
 */
export async function createCheckoutSession(
  tier: 'pro' | 'team',
  email?: string
): Promise<CheckoutSession> {
  const result = await createPolarCheckout(tier, email);
  return { url: result.checkout_url };
}

/**
 * @deprecated Use createPolarPortalSession instead
 */
export async function createPortalSession(): Promise<{ url: string }> {
  return createPolarPortalSession();
}

// ============================================
// Sync API (Supabase 사용자 ↔ 라이선스 연동)
// ============================================

/**
 * 사용자 라이선스 정보 조회 (Supabase 인증 기반)
 */
export async function getUserLicenses(): Promise<License[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  const result = await fetchApi<{
    success: true;
    licenses: License[];
  }>('/sync/user-licenses', {
    method: 'GET',
    headers: {
      'X-Supabase-Token': session.access_token,
    },
  });
  
  return result.licenses;
}

// ============================================
// 앱 연동 코드 (Link Code)
// ============================================

export interface LinkCodeResponse {
  code: string;
  expires_at: string;
}

/**
 * 앱 연동 코드 생성
 * 데스크톱 앱에서 계정 연동시 사용
 */
export async function createLinkCode(): Promise<LinkCodeResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new ApiException('UNAUTHORIZED', '로그인이 필요합니다');
  }
  
  const result = await fetchApi<{
    success: true;
    code: string;
    expires_at: string;
  }>('/link-codes', {
    method: 'POST',
    headers: {
      'X-Supabase-Token': session.access_token,
    },
  });
  
  return {
    code: result.code,
    expires_at: result.expires_at,
  };
}
